import { HAS_SEEN_PULSING_TABLE_GUIDANCE } from 'webReservations/components/InteractiveGuestFloorPlan';
import { Availability } from 'webReservations/restaurant/apiHelpers';
import { isBookedAsBestAvailable } from 'webReservations/utils/isBookedAsBestAvailable';
import { Icon } from '@components/icon/Icon';
import { centsToCompactDollar } from '@utils/currency';
import { ISOTimeTo12HourTime } from '@utils/time';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import { CheckoutButton } from '../checkout/CheckoutButton';
import { useAvailabilityContext } from '../context/AvailabilityContext';
import styles from './AvailabilityItem.scss';

export interface AvailabilityItemProps {
  availability: Availability;
}

export const AvailabilityItem = ({ availability }: AvailabilityItemProps) => {
  const {
    listing: { id: listingId, iconName, publicName, floorPlanTableCount },
    purchasePrice,
    time,
    date,
    isBestAvailable,
    isResale,
  } = availability;

  const isOffer = availability.isBiddable && !availability.isBuyable;

  const {
    clearSelectedAvailability,
    selectedAvailability,
    setSelectedAvailability,
    selectedFloorPlanListingIds,
  } = useAvailabilityContext();

  const isSelected =
    selectedAvailability && selectedAvailability.listingId === listingId;

  const wasAutoSelectedBestAvailable = isBookedAsBestAvailable({
    isBestAvailable,
    listingId,
    selectedFloorPlanListingIds,
  });

  const availabilityIsForTableCombination = floorPlanTableCount > 1;

  const availabilityName = () => {
    if (wasAutoSelectedBestAvailable) return 'Best Available';
    if (availabilityIsForTableCombination) {
      return publicName ? publicName : `${floorPlanTableCount} Tables`;
    }
    return publicName;
  };

  const handleOnClick = () => {
    if (selectedAvailability && isSelected) {
      clearSelectedAvailability();
    } else {
      setSelectedAvailability({
        isOffer,
        isResale,
        isBestAvailable: wasAutoSelectedBestAvailable,
        purchasePrice,
        listingId,
        publicName,
        time,
        date,
      });

      if (localStorage.getItem(HAS_SEEN_PULSING_TABLE_GUIDANCE) !== 'true') {
        window.localStorage.setItem(HAS_SEEN_PULSING_TABLE_GUIDANCE, 'true');
      }
    }
  };

  const containerStyles = cx(styles.container, {
    [styles.isSelected]: isSelected,
    [styles.isBestAvailable]: isBestAvailable,
    [styles.isBestAvailableAndOfferOnly]:
      wasAutoSelectedBestAvailable && !availability.isBuyable,
  });

  return (
    <li
      className={containerStyles}
      data-testid={`${listingId}-${isOffer ? 'offer' : 'buy'}`}
    >
      <button onClick={handleOnClick} type="button">
        <Icon name={iconName} />

        <div className={styles.column}>
          <div className={styles.row}>
            <span className={cx(typography.c3_20, styles.time)}>
              {ISOTimeTo12HourTime(time)}
            </span>
          </div>

          <div className={cx(typography.t2, styles.description)}>
            {availabilityName()}
          </div>
        </div>

        <div
          data-testid="buy-or-make-offer"
          className={cx(typography.h7m, styles.buyOrMakeOffer)}
        >
          {isOffer ? 'Offer' : centsToCompactDollar(purchasePrice!)}
        </div>
      </button>
      {isSelected && <CheckoutButton isOffer={isOffer} />}
    </li>
  );
};
